import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { getCountryLabel } from "util/countryDisplay";
import { formatDeparture } from "util/departureDisplay";
import { useHistory } from "react-router-dom";
import MaterialTable from "material-table";
import DeleteSharpIcon from "@material-ui/icons/DeleteSharp";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { getLocalization } from "util/table-utils";
import { PATHS } from "util/appConstants";
import {
  getZipcodes,
  deleteZipcodes,
  bulkUpdateZipcodes,
  selectZipcodeStatus,
  selectZipcodes,
  selectZipcodesTimestamp,
} from "redux/slices/zipcodeSlice";
import { getTours, selectTours } from "redux/slices/tourSlice";
import { getSuppliers, selectSuppliers } from "redux/slices/supplierSlice";
import withConfirm from "components/dialogs/delete";
import Navbar from "components/Navbar";
import CustomersNavbar from "components/Masterbar/CustomersBar";
import DarkLayout from "components/Shared/DarkLayout";
import { selectUser } from "redux/slices/userSlice";
import { IconButton } from "@material-ui/core";
import {
  groupZipcodesForZipcodeTable,
  pinSelectedZipcodeTableRowFilter,
  selectionHasUniformSuppliers,
  selectionHasUniformTour,
  uniqueZipcodeTourTargets,
  zipcodeTableSelectionRefs,
} from "util/zipcodeListGroup";
import { ZipcodesBulkDialogs } from "components/Zipcodes/ZipcodesBulkDialogs";
import {
  ZipcodesListToolbarContext,
  zipcodesTableComponents,
} from "components/Zipcodes/ZipcodesListToolbar";

const FilterIcon = styled("i")(() => ({
  color: "#525252",
  fontSize: "12px",
}));

const tableTitle = "ZIPCODES";

const tableIcons = {
  Filter: () => <FilterIcon className={clsx("fas fa-filter")} />,
};

const zipcodeTableRowFilter = pinSelectedZipcodeTableRowFilter(zipcodeTableSelectionRefs);

const tableOptions = {
  selection: true,
  pageSize: 50,
  pageSizeOptions: [50, 100],
  actionsColumnIndex: -1,
  searchFieldAlignment: "left",
  showTitle: false,
  showTextRowsSelected: false,
  filtering: true,
  emptyRowsWhenPaging: false,
  headerStyle: {
    backgroundColor: "#121212",
    color: "white",
    borderBottom: "1px solid #525252",
    font: "normal normal normal 12px/24px Roboto",
    fontWeight: "bold",
  },
  cellStyle: {
    backgroundColor: "#121212",
    color: "white",
    border: "none",
    font: "normal normal normal 12px/24px Roboto",
    padding: "0 16px",
  },
  searchFieldStyle: {
    color: "#F5F5F5",
  },
  filterCellStyle: {
    color: "#F5F5F5",
  },
  rowStyle: { height: "38px" },
};

const ZipcodesList = ({ confirm }) => {
  const { t, i18n } = useTranslation("common");
  const dispatch = useDispatch();
  const history = useHistory();
  const loading = useSelector(selectZipcodeStatus);
  const zipcodes = useSelector(selectZipcodes);
  const timestamp = useSelector(selectZipcodesTimestamp);
  const user = useSelector(selectUser);
  const tours = useSelector(selectTours);
  const suppliers = useSelector(selectSuppliers);

  const [selectedRows, setSelectedRows] = useState([]);
  const [openSupplier, setOpenSupplier] = useState(false);
  const [openTour, setOpenTour] = useState(false);
  const [tableResetKey, setTableResetKey] = useState(0);

  const groupedData = useMemo(() => groupZipcodesForZipcodeTable(zipcodes, t), [zipcodes, t]);
  zipcodeTableSelectionRefs.groupedData = groupedData;

  const selectedRowIdsKey = useMemo(
    () =>
      selectedRows
        .map((row) => row.id)
        .sort()
        .join(","),
    [selectedRows]
  );

  const deliveryTours = useMemo(
    () =>
      (tours || []).filter(
        (tour) => (tour.tour_type_category ?? tour.tour_category) !== "pickup"
      ),
    [tours]
  );

  useEffect(() => {
    if (!loading && !timestamp) {
      dispatch(getZipcodes());
    }
  }, [dispatch, loading, timestamp]);

  useEffect(() => {
    dispatch(getTours());
    dispatch(getSuppliers());
  }, [dispatch]);

  const clearTableSelection = useCallback(() => {
    setSelectedRows([]);
    zipcodeTableSelectionRefs.pinnedRowIds = new Set();
  }, []);

  const prevTimestampRef = useRef(undefined);
  useEffect(() => {
    if (timestamp == null) {
      return;
    }
    if (
      prevTimestampRef.current !== undefined &&
      prevTimestampRef.current !== timestamp
    ) {
      setTableResetKey((key) => key + 1);
    }
    prevTimestampRef.current = timestamp;
  }, [timestamp]);

  const showReassignSupplier =
    selectedRows.length > 0 && selectionHasUniformSuppliers(selectedRows);
  const showReassignTour = selectedRows.length > 0 && selectionHasUniformTour(selectedRows);

  const callbackOnDelete = useCallback(
    (rowData) => {
      const targets = uniqueZipcodeTourTargets([rowData]);
      if (!targets.length) {
        return;
      }
      confirm(
        () => {
          dispatch(deleteZipcodes(targets)).then(() => dispatch(getZipcodes()));
        },
        {
          description: t("Delete zipcode?"),
        }
      );
    },
    [confirm, dispatch, t]
  );

  const deleteHandlerRef = useRef(callbackOnDelete);
  deleteHandlerRef.current = callbackOnDelete;

  const canDestroyZipcodes = Boolean(user?.permissions?.zipcodes?.destroy);
  const canCreateZipcodes = Boolean(user?.permissions?.zipcodes?.create);

  const handleBulkSupplier = async (payload) => {
    clearTableSelection();
    await dispatch(
      bulkUpdateZipcodes({
        action: "reassign_supplier",
        ...payload,
      })
    );
    setOpenSupplier(false);
  };

  const handleBulkTour = async (payload) => {
    clearTableSelection();
    await dispatch(
      bulkUpdateZipcodes({
        action: "reassign_tour",
        ...payload,
      })
    );
    setOpenTour(false);
  };

  const handleBulkDelete = useCallback(() => {
    const targets = uniqueZipcodeTourTargets(selectedRows);
    if (!targets.length) {
      return;
    }

    confirm(
      () => {
        clearTableSelection();
        dispatch(deleteZipcodes(targets)).then(() => dispatch(getZipcodes()));
      },
      {
        description: t("Delete zipcode?"),
      }
    );
  }, [clearTableSelection, confirm, dispatch, selectedRows, t]);

  const tableActions = useMemo(() => {
    if (!canCreateZipcodes) {
      return [];
    }
    return [
      {
        icon: "add",
        tooltip: t("Add"),
        iconProps: {
          style: { color: "#1F1F1F", background: "#6F9CEB", marginRight: "15px" },
        },
        isFreeAction: true,
        onClick: () => history.push(PATHS.zipcodes.add),
        position: "row",
      },
    ];
  }, [canCreateZipcodes, history, t]);

  const tableColumns = useMemo(
    () => [
      {
        title: t("Zipcode"),
        field: "zipcode",
        customFilterAndSearch: zipcodeTableRowFilter,
      },
      {
        title: t("Supplier"),
        field: "supplierLabel",
        sorting: false,
        customFilterAndSearch: zipcodeTableRowFilter,
      },
      {
        title: t("Country"),
        field: "country",
        render: (rowData) => getCountryLabel(rowData.country, t, i18n),
        customSort: (rowA, rowB) =>
          getCountryLabel(rowA.country, t, i18n).localeCompare(
            getCountryLabel(rowB.country, t, i18n),
          ),
        customFilterAndSearch: (filterValue, row) => {
          if (!filterValue) {
            return true;
          }
          const code = String(row.country ?? "").toUpperCase();
          const label = getCountryLabel(row.country, t, i18n);
          const needle = String(filterValue).trim().toUpperCase();
          return code.includes(needle) || label.toUpperCase().includes(needle);
        },
      },

      {
        title: t("Tour"),
        field: "tourLabel",
        customFilterAndSearch: zipcodeTableRowFilter,
        render: (rowData) => {
          const entries = rowData._tourEntries || [];
          if (entries.length > 0) {
            return (
              <div>
                {entries.map((entry, idx) => {
                  const depText =
                    entry.departures?.length > 0
                      ? ` (${entry.departures.map((d) => formatDeparture(d, t)).join(", ")})`
                      : "";
                  return (
                    <div key={idx}>
                      {entry.name}{depText}
                    </div>
                  );
                })}
              </div>
            );
          }
          return rowData.tourLabel || "";
        },
      },
      {
        title: t("Actions"),
        render: (rowData) => {
          if (!canDestroyZipcodes) {
            return null;
          }

          return (
            <IconButton
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                deleteHandlerRef.current(rowData);
              }}
            >
              <DeleteSharpIcon />
            </IconButton>
          );
        },
      },
    ],
    [canDestroyZipcodes, t, i18n, tableResetKey]
  );

  const tableLocalization = useMemo(() => getLocalization(t), [t]);

  const handleSelectionChange = useCallback((rows) => {
    const next = rows || [];
    zipcodeTableSelectionRefs.pinnedRowIds = new Set(next.map((row) => row.id));
    setSelectedRows(next);
  }, []);

  const handleRowClick = useCallback(
    (e, rowData) => {
      const tourId = rowData._tourId;
      const country = rowData._country ?? rowData.country;
      const path = PATHS.zipcodes.edit.replace(
        ":zipcode",
        encodeURIComponent(rowData.zipcode)
      );
      const params = new URLSearchParams();
      if (country) {
        params.set("country", country);
      }
      if (tourId != null) {
        params.set("tour_id", String(tourId));
      }
      const search = params.toString() ? `?${params.toString()}` : "";
      history.push(`${path}${search}`);
    },
    [history]
  );

  const toolbarContextValue = useMemo(
    () => ({
      selectionCount: selectedRows.length,
      selectedRowIdsKey,
      groupedData,
      showReassignSupplier,
      showReassignTour,
      showBulkDelete: canDestroyZipcodes,
      onReassignSupplier: () => setOpenSupplier(true),
      onReassignTour: () => setOpenTour(true),
      onBulkDelete: handleBulkDelete,
      t,
    }),
    [
      selectedRows.length,
      selectedRowIdsKey,
      groupedData,
      showReassignSupplier,
      showReassignTour,
      canDestroyZipcodes,
      handleBulkDelete,
      t,
    ]
  );

  return (
    <>
      <Navbar />
      <CustomersNavbar />
      <DarkLayout doublebar loading={loading}>
        <div key={`zipcodes-table-${tableResetKey}`} className="custom-table-styles">
          <ZipcodesListToolbarContext.Provider value={toolbarContextValue}>
            <MaterialTable
              icons={tableIcons}
              style={{ display: "flex", flexDirection: "column" }}
              data={groupedData}
              title={t(tableTitle)}
              columns={tableColumns}
              actions={tableActions}
              localization={tableLocalization}
              onSelectionChange={handleSelectionChange}
              onRowClick={handleRowClick}
              components={zipcodesTableComponents}
              options={tableOptions}
            />
          </ZipcodesListToolbarContext.Provider>
        </div>
        <ZipcodesBulkDialogs
          openSupplier={openSupplier}
          openTour={openTour}
          selectedRows={selectedRows}
          supplierList={suppliers}
          deliveryTours={deliveryTours}
          onCloseSupplier={() => setOpenSupplier(false)}
          onCloseTour={() => setOpenTour(false)}
          onSubmitSupplier={handleBulkSupplier}
          onSubmitTour={handleBulkTour}
        />
      </DarkLayout>
    </>
  );
};

export default withConfirm(ZipcodesList);
